/**
 * Typed calls for the Transport page (#214). The shapes mirror the zod schemas in
 * api/src/transport/routes.ts; `/openapi.json` is the contract. The change_set RECORD is read
 * through /api/entities?type=change_set (rule 1); everything else is /api/transport/*.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '@/api/client';

export type ChangeSetStatus = 'open' | 'released' | 'imported' | 'discarded';

export interface TransportEnvironment {
  code: string;
  label: string;
  branch: string;
  head: string | null;
  isActive: boolean;
  reachable: boolean;
  deployment: EnvironmentDeployment;
  /** #488: why the environment's head differs from (or matches) the deployed sha; null = nothing to explain. */
  headExplanation?: string | null;
}

/** #402: the latest deployment the forge records for an environment; source none = no forge record. */
export interface EnvironmentDeployment {
  source: 'forge' | 'delivery' | 'none';
  sha: string | null;
  digest: string | null;
  state: string | null;
  at: string | null;
  url: string | null;
  creator: string | null;
  note: string | null;
}

export interface TransportRoute {
  from: string;
  to: string;
  gateRule: 'none' | 'facilitator' | 'cc1_scope';
  ordinal: number;
  isActive: boolean;
}

export interface TransportStatus {
  configured: boolean;
  problem: string | null;
  mode: 'local' | 'github' | null;
  location: string | null;
  instanceEnvironment: string;
  releaseLine: string;
  environments: TransportEnvironment[];
  routes: TransportRoute[];
  phases: string[];
  forge: {
    configured: boolean;
    kind: 'github' | 'gitea' | 'dryrun' | null;
    location: string | null;
    credential: 'forge_app' | 'transport_app' | 'token' | 'test' | null;
    problem: string | null;
  };
  /** #401: contained writes waiting to be committed to their change set's branch. */
  pendingExports: number;
  waitingReleases: { key: string; pr: number | null }[];
}

export interface ChangeSet {
  id: string;
  key: string;
  name: string;
  description: string | null;
  authorUserId: string | null;
  authorName: string | null;
  status: ChangeSetStatus;
  branch: string;
  originEnvironment: string;
  releasedTag: string | null;
  releasedAt: string | null;
  createdAt: string;
}

export interface Approval {
  changeLogId: string;
  userId: string;
  name: string | null;
  at: string;
  note: string | null;
  reviewUrl: string | null;
}

export interface Gate {
  categories: string[];
  twoPersonCategories: string[];
  required: number;
  approvals: Approval[];
  met: boolean;
  rule: string;
}

export interface ContainedWrite {
  changeLogId: string;
  action: string;
  at: string;
  actorUserId: string | null;
  actorName: string | null;
  category: string;
  label: string;
  objectKeys: string[];
  commit: string | null;
  changeLogIds: string[];
  /** #401: contained, not yet exported to the set's branch. */
  pending: boolean;
}

export interface TransportPullRequest {
  state: 'opened' | 'skipped' | 'merged';
  target: string;
  number: number | null;
  url: string | null;
  reason: string | null;
  at: string;
}

export interface TransportReviewers {
  environment: string;
  required: number;
  reviewers: { userId: string; name: string; login: string | null }[];
  applied: boolean;
  detail: string;
  at: string | null;
}

export interface ChangeSetDetail {
  changeSet: ChangeSet;
  head: string | null;
  contained: ContainedWrite[];
  gate: Gate;
  lockWarnings: { objectKey: string; heldBy: string }[];
  pendingExports: number;
  pullRequest: TransportPullRequest | null;
  /** #399: the release is on the release line (false while its PR waits to merge). */
  landed: boolean;
  reviewers: TransportReviewers[];
}

export interface UncontainedWrite {
  changeLogId: string;
  at: string;
  actorUserId: string | null;
  actorName: string | null;
  category: string;
  label: string;
  objectKeys: string[];
  changeLogIds: string[];
  note: string | null;
}

export interface Preview {
  changeSet: string;
  status: string;
  environment: string;
  releaseTag: string | null;
  sha: string | null;
  objects: { key: string; change: 'created' | 'modified' | 'deleted'; category: string; before: string | null; after: string | null }[];
  counts: { created: number; modified: number; deleted: number };
  missingReferences: { object: string; references: string }[];
  destructive: { object: string; operation: string }[];
  categories: string[];
  dependencies: { key: string; tag: string | null; satisfied: boolean }[];
  conflicts: string[];
}

export interface QueueItem {
  position: number;
  changeSetId: string;
  key: string;
  name: string;
  tag: string;
  sha: string | null;
  releasedAt: string | null;
  wouldOvertake: string[];
}

export interface PhaseResult {
  phase: string;
  returnCode: 0 | 4 | 8 | 12;
  messages: string[];
}

export interface ImportRun {
  id: string;
  releaseTag: string;
  releaseSha: string;
  changeSet: string | null;
  environment: string;
  phase: string;
  returnCode: number | null;
  phaseResults: PhaseResult[];
  startedAt: string;
  finishedAt: string | null;
  actorName: string | null;
  message: string | null;
  preSnapshotRef: string | null;
}

/** One change_set row from /api/entities — the attributes the transport service writes. */
export interface ChangeSetRow {
  id: string;
  key: string;
  name: string;
  status: ChangeSetStatus;
  statusLabel: string;
  statusColor: string | null;
  branch: string;
  originEnvironment: string;
  releasedTag: string | null;
  releasedAt: string | null;
}

type Cell = { value?: unknown; label?: unknown; color?: unknown } | undefined;
const str = (c: Cell) => (typeof c?.value === 'string' && c.value.length > 0 ? c.value : null);

/** Maps an /api/entities row (attrs keyed by attribute code) to the list's row shape. */
export function toChangeSetRow(raw: { id: string; key: string; name: string; attrs?: Record<string, Cell> }): ChangeSetRow {
  const a = raw.attrs ?? {};
  const status = (str(a.change_set_status) ?? 'open') as ChangeSetStatus;
  return {
    id: raw.id,
    key: raw.key,
    name: raw.name,
    status,
    statusLabel: typeof a.change_set_status?.label === 'string' ? a.change_set_status.label : status,
    statusColor: typeof a.change_set_status?.color === 'string' ? a.change_set_status.color : null,
    branch: str(a.change_set_branch) ?? '',
    originEnvironment: str(a.origin_environment) ?? '',
    releasedTag: str(a.released_tag),
    releasedAt: str(a.released_at),
  };
}

export const transportKeys = {
  all: ['cigs', 'transport'] as const,
  status: ['cigs', 'transport', 'status'] as const,
  list: ['cigs', 'transport', 'change-sets'] as const,
  detail: (id: string) => ['cigs', 'transport', 'change-set', id] as const,
  preview: (id: string, env: string) => ['cigs', 'transport', 'preview', id, env] as const,
  uncontained: ['cigs', 'transport', 'uncontained'] as const,
  queue: (env: string) => ['cigs', 'transport', 'queue', env] as const,
  imports: ['cigs', 'transport', 'imports'] as const,
};

/** GET /status. A 404 (no transport routes in this build) is "unreachable", never an error page. */
export function useTransportStatus(enabled = true) {
  return useQuery<TransportStatus | null>({
    queryKey: transportKeys.status,
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return await apiFetch<TransportStatus>('/transport/status');
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}

export function useChangeSets(enabled = true) {
  return useQuery<ChangeSetRow[]>({
    queryKey: transportKeys.list,
    enabled,
    queryFn: async () => {
      const raw = await apiFetch<{ rows: { id: string; key: string; name: string; attrs?: Record<string, Cell> }[] }>(
        '/entities?type=change_set&pageSize=500',
      );
      return (raw.rows ?? []).map(toChangeSetRow);
    },
  });
}

const detailQuery = (id: string) => ({
  queryKey: transportKeys.detail(id),
  queryFn: () => apiFetch<ChangeSetDetail>(`/transport/change-sets/${encodeURIComponent(id)}`),
});

export function useChangeSet(id: string | null) {
  return useQuery<ChangeSetDetail>({ ...detailQuery(id ?? 'none'), enabled: Boolean(id) });
}

/** Author per change set — the entity list does not carry it, the detail does. */
export function useChangeSetDetails(ids: string[], enabled = true) {
  return useQueries({ queries: ids.map((id) => ({ ...detailQuery(id), enabled, staleTime: 30_000 })) });
}

export function usePreview(id: string | null, environment: string | null) {
  return useQuery<Preview>({
    queryKey: transportKeys.preview(id ?? 'none', environment ?? ''),
    enabled: Boolean(id && environment),
    retry: false,
    queryFn: () => apiFetch<Preview>(`/transport/change-sets/${encodeURIComponent(id!)}/preview?environment=${encodeURIComponent(environment!)}`),
  });
}

export function useUncontained(enabled = true) {
  return useQuery<UncontainedWrite[]>({
    queryKey: transportKeys.uncontained,
    enabled,
    queryFn: async () => (await apiFetch<{ items: UncontainedWrite[] }>('/transport/uncontained')).items,
  });
}

export function useQueue(environment: string | null) {
  return useQuery<QueueItem[]>({
    queryKey: transportKeys.queue(environment ?? ''),
    enabled: Boolean(environment),
    queryFn: async () => (await apiFetch<{ items: QueueItem[] }>(`/transport/queue?environment=${encodeURIComponent(environment!)}`)).items,
  });
}

export function useImports(enabled = true) {
  return useQuery<ImportRun[]>({
    queryKey: transportKeys.imports,
    enabled,
    queryFn: async () => (await apiFetch<{ items: ImportRun[] }>('/transport/imports?limit=200')).items,
  });
}

/** Every transport write invalidates every transport read: the landscape moves as one. */
function useTransportWrite<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation<O, unknown, I>({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: transportKeys.all });
      void qc.invalidateQueries({ queryKey: ['cigs', 'history'] });
    },
  });
}

const post = <O>(path: string, body: unknown) => apiFetch<O>(path, { method: 'POST', body });


export const useOpenChangeSet = () =>
  useTransportWrite((b: { name: string; description?: string | null }) => post<ChangeSet>('/transport/change-sets', b));
export const useAdopt = () =>
  useTransportWrite((b: { id: string; changeLogIds: string[] }) =>
    post<{ commit: string; objectKeys: string[] }>(`/transport/change-sets/${encodeURIComponent(b.id)}/adopt`, { changeLogIds: b.changeLogIds }),
  );
export const useApprove = () => useTransportWrite((b: { id: string; note?: string }) => post<Gate>(`/transport/change-sets/${encodeURIComponent(b.id)}/approvals`, { note: b.note || undefined }));
/** G53 (#474): a release always carries its reason — under a single-admin requirement the server answers 422 reason_required without one. */
export const releaseBody = (reason: string) => ({ reason: reason.trim() });
export const useRelease = () =>
  useTransportWrite((b: { id: string; reason: string }) => post<{ changeSet: ChangeSet; tag: string; sha: string; pullRequest: TransportPullRequest | null }>(`/transport/change-sets/${encodeURIComponent(b.id)}/release`, releaseBody(b.reason)));
export const useDiscard = () => useTransportWrite((b: { id: string; reason: string }) => post<ChangeSet>(`/transport/change-sets/${encodeURIComponent(b.id)}/discard`, { reason: b.reason }));
export const usePromote = () =>
  useTransportWrite((b: { changeSet: string; environment: string }) => post<ImportRun>('/transport/imports', b));
export const useRestart = () => useTransportWrite((b: { id: string }) => post<ImportRun>(`/transport/imports/${encodeURIComponent(b.id)}/restart`, {}));
